import { createSlice } from "@reduxjs/toolkit"
import { api } from "../api"
import AvaImg from "assets/images/ava.png"
import { RoleLabel } from "constant/Role"
import { applyProcurementDevOverride } from "shared/procurement/devOverride"
import { preferProcurementManager } from "shared/procurement/eligibleRoles"

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]))
  } catch {
    return null
  }
}

/**
 * Use login centerId when present; otherwise fall back to the first centerIds
 * entry. `0` is the "no center" sentinel (see `initialState`), so it counts as
 * absent — a role with centers must not stay pinned to it.
 */
export const resolveDefaultCenterId = (centerId, centerIds = []) => {
  if (centerId != null && centerId !== 0) return centerId
  if (Array.isArray(centerIds) && centerIds.length > 0) return centerIds[0]
  return 0
}

const emptyProcurement = {
  defaultRegion: null,
}

const initialState = {
  accessToken: "",
  refreshToken: "",
  user: {},
  role: null,
  roles: [],
  centerId: 0,
  centerIds: [],
  procurement: emptyProcurement,
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logOut: () => initialState,
    setTokens: (state, { payload }) => {
      state.accessToken = payload.accessToken
      state.refreshToken = payload.refreshToken
    },
    setUser: (state, { payload }) => {
      state.user = payload.user
    },
    setEmpCode: (state, { payload }) => {
      state.user.empCode = payload.empCode
    },
    setRole: (state, { payload }) => {
      state.role = payload.role
    },
    // Switch the active role for a multi-role user. Stores the role's label in
    // `state.role` (the same field login sets), so every menu/route/role check
    // that reads `auth.role` follows the switch with no extra plumbing.
    setActiveRoleByCode: (state, { payload }) => {
      state.role = RoleLabel[payload.code] || state.role
      state.centerId = resolveDefaultCenterId(state.centerId, state.centerIds)
    },
    setCentersFromUser: (state, { payload }) => {
      state.centerIds = payload.centerIds || []
      state.centerId = resolveDefaultCenterId(
        payload.centerId,
        payload.centerIds
      )
    },
    setCenterId: (state, { payload }) => {
      state.centerId = payload.centerId
    },
    setIsManager: (state, { payload }) => {
      state.user.isManager = payload.isManager
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      api.endpoints.loginV2.matchFulfilled,
      (state, { payload }) => {
        state.accessToken = payload.accessToken
        state.refreshToken = payload.refreshToken
        state.user = {
          ...payload.user,
          avatar: payload.user.avatar ?? AvaImg,
        }
        const roles = parseJwt(payload.accessToken)?.roles || []
        state.role = RoleLabel[preferProcurementManager(payload.role, roles)] || null
        state.roles = roles
        state.centerIds = payload.user.centerIds || []
        state.centerId = resolveDefaultCenterId(
          payload.user.centerId,
          state.centerIds
        )
        state.procurement = {
          defaultRegion: payload?.procurement?.defaultRegion || null,
        }
      }
    )
    builder.addMatcher(
      api.endpoints.ssoLogin.matchFulfilled,
      (state, { payload }) => {
        state.accessToken = payload.data.accessToken
        state.refreshToken = payload.data.refreshToken
        state.user = {
          ...payload.data.user,
          avatar: payload.data.user.avatar ?? AvaImg,
        }
        const roles = parseJwt(payload.data.accessToken)?.roles || []
        state.role = RoleLabel[preferProcurementManager(payload.data.role, roles)] || null
        state.roles = roles
        state.centerIds = payload.data.user.centerIds || []
        state.centerId = resolveDefaultCenterId(
          payload.data.user.centerId,
          state.centerIds
        )
        state.procurement = {
          defaultRegion: payload.data?.procurement?.defaultRegion || null,
        }
      }
    )
  },
})

export default authSlice.reducer

export const {
  setTokens,
  logOut,
  setUser,
  setEmpCode,
  setRole,
  setActiveRoleByCode,
  setCenterId,
  setCentersFromUser,
  setIsManager,
} = authSlice.actions

export const selectAuth = (state) => state.auth

export const selectRoles = (state) => state.auth?.roles || []

export const selectProcurement = (state) =>
  applyProcurementDevOverride(state.auth?.procurement || emptyProcurement)
